import { X } from "lucide-react";
import type { TaskDetailProps } from "../types";
import { FocusCheckbox } from "./TaskRow";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Centered sheet: title, notes, checklist, then one row of property chips. */
export function FocusTaskDetail({ task, mode, onClose, onToggleComplete, parts }: TaskDetailProps) {
  const closed = task.status === "done" || task.status === "cancelled";
  const props = parts.properties;
  return (
    <div
      data-task-detail={task.id}
      role="group"
      aria-label={`Task ${task.readableId}`}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      className={cn(
        "relative flex flex-col bg-popover text-popover-foreground",
        mode === "inline" && "my-2 animate-expand rounded-xl border border-border shadow-card",
        mode === "panel" && "h-full",
      )}
    >
      <div className="flex items-start gap-3.5 px-6 pb-1 pt-5">
        <span className="flex h-[29px] items-center">
          <FocusCheckbox
            size={18}
            checked={closed}
            cancelled={task.status === "cancelled"}
            onToggle={onToggleComplete}
            label={closed ? "Reopen task" : "Complete task"}
          />
        </span>
        <div className="focus-detail-title min-w-0 flex-1">{parts.title}</div>
        <Button size="icon" variant="quiet" aria-label="Close detail" onClick={onClose} className="-mr-2 mt-0.5">
          <X />
        </Button>
      </div>
      <div className="min-h-16 pb-3 pl-[56px] pr-6">
        {parts.syncNotice}
        {parts.notes}
        <div className="mt-1.5">{parts.checklist}</div>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border/70 px-4 py-2.5">
        <div data-detail-chips className="focus-detail-chips flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {props.status}
          {props.priority}
          {props.when}
          {props.deadline}
          {props.repeat}
          {props.labels}
          {props.location}
          {props.reminder}
          {props.snooze}
        </div>
        <div className="ml-auto flex items-center gap-0.5">{parts.actions}</div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 pb-3 text-[12px] text-subtle">{parts.meta}</div>
    </div>
  );
}
